import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  LucideAngularModule,
  Check, Save, Lock, RotateCcw, ArrowRight,
} from 'lucide-angular';

import { SrDocLoadingService } from './sr-doc-loading.service';

/**
 * Modal de carga animado para flujos de emisión de DTE.
 *
 * Renderiza un documento con líneas que se revelan progresivamente, un track
 * de progreso segmentado (o indeterminado) y un timbre SII que cae al final
 * cuando el scenario lo requiere. Cuando el caller llama a
 * `SrDocLoadingService.complete()` muestra una pantalla OK con título, pills
 * y un CTA para navegar al detalle del documento.
 *
 * El componente lee todo su state del servicio singleton — no recibe inputs.
 * Se monta una sola vez en el `app.component.html` raíz.
 */
@Component({
  selector: 'sr-doc-loading',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './sr-doc-loading.component.html',
  styleUrl: './sr-doc-loading.component.scss',
})
export class SrDocLoadingComponent implements OnInit, OnDestroy {
  private readonly svc = inject(SrDocLoadingService);
  private readonly router = inject(Router);

  @Output() closed = new EventEmitter<void>();

  readonly icons = { Check, Save, Lock, RotateCcw, ArrowRight };

  readonly state = this.svc.state;

  /** Total de líneas a revelar dentro del documento dibujado. */
  readonly docLines: number[] = [1, 2, 3, 4, 5, 6];

  /**
   * Patrón PDF417 simulado del timbre SII. Se genera una sola vez con un
   * patrón pseudo-aleatorio determinístico (no varía entre renders).
   */
  readonly stampBars: boolean[] = (() => {
    const bars: boolean[] = [];
    let seed = 0x1f3a5;
    for (let i = 0; i < 28 * 9; i++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      bars.push((seed & 1) === 1);
    }
    return bars;
  })();

  /** Cantidad de líneas a revelar según el paso actual (proporcional). */
  readonly linesRevealed = computed(() => {
    const s = this.state();
    if (!s.config) return 0;
    if (s.done) return this.docLines.length;
    const total = s.config.steps.length;
    const reveal = Math.ceil(((s.currentStep + 1) / total) * this.docLines.length);
    return Math.min(reveal, this.docLines.length);
  });

  /** True cuando el timbre debe caer (último paso + stamp habilitado + done). */
  readonly stampDrop = computed(() => {
    const s = this.state();
    return !!(s.config?.stamp && s.done);
  });

  /** True cuando el documento debe "settled" (rotación a 0 + glow verde). */
  readonly docSettled = computed(() => this.stampDrop());

  /** Caption de paso ("Paso 2 de 5") visible cuando hay más de 1 paso. */
  readonly stepCaption = computed(() => {
    const s = this.state();
    if (!s.config || s.config.steps.length <= 1) return '';
    return `Paso ${s.currentStep + 1} de ${s.config.steps.length}`;
  });

  /** Texto principal del status. Cambia a "Timbre recibido" al completar con stamp. */
  readonly statusLabel = computed(() => {
    const s = this.state();
    if (!s.config) return '';
    if (s.done && s.config.stamp) return 'Timbre recibido';
    return s.config.steps[s.currentStep] ?? '';
  });

  ngOnInit(): void { /* nothing — state from service signal */ }

  ngOnDestroy(): void { /* nothing — service is singleton */ }

  /**
   * CTA principal del estado OK. Navega al path si está definido, después cierra.
   */
  onPrimaryCta(): void {
    const s = this.state();
    const path = s.result?.navigateTo;
    this.svc.close();
    this.closed.emit();
    if (path) this.router.navigateByUrl(path);
  }

  /** Cierra el modal sin navegar. */
  onClose(): void {
    this.svc.close();
    this.closed.emit();
  }
}
